import * as THREE from 'three';
import { BoxCollider } from '../physics/Collider.js';

const _local = new THREE.Vector3();

/**
 * Subtract axis aligned rectangles from a rectangle, returning a cover of the
 * remainder. Guillotine style: each hole splits every surviving rect into at
 * most four pieces. Exact for the handful of holes a portal gun can make.
 */
export function subtractRects(outer, holes) {
  let rects = [{ ...outer }];
  for (const h of holes) {
    const next = [];
    for (const r of rects) {
      const overlap = h.u0 < r.u1 && h.u1 > r.u0 && h.v0 < r.v1 && h.v1 > r.v0;
      if (!overlap) { next.push(r); continue; }
      const cu0 = Math.max(r.u0, h.u0);
      const cu1 = Math.min(r.u1, h.u1);
      if (r.u0 < cu0) next.push({ u0: r.u0, u1: cu0, v0: r.v0, v1: r.v1 });
      if (cu1 < r.u1) next.push({ u0: cu1, u1: r.u1, v0: r.v0, v1: r.v1 });
      if (r.v0 < h.v0) next.push({ u0: cu0, u1: cu1, v0: r.v0, v1: h.v0 });
      if (h.v1 < r.v1) next.push({ u0: cu0, u1: cu1, v0: h.v1, v1: r.v1 });
    }
    rects = next;
  }
  return rects.filter((r) => r.u1 - r.u0 > 1e-3 && r.v1 - r.v0 > 1e-3);
}

const HOLE_DEPTH = 0.55;

/**
 * Keeps the collision world consistent with where portals have been placed.
 *
 * A portal must be walkable-through, but simply disabling the wall it sits in
 * would let the player stroll through the whole slab. Instead the host box is
 * disabled and replaced by a set of generated boxes that reproduce it exactly,
 * minus a shallow rectangular tunnel behind each portal.
 */
export class PortalHoleManager {
  constructor(world) {
    this.world = world;
    /** @type {Map<number, {collider: BoxCollider, holes: Map<string, object>, generated: BoxCollider[]}>} */
    this.entries = new Map();
  }

  /**
   * @param {BoxCollider} collider host box
   * @param {string} portalId
   * @param {{axis:number, sign:number, u:number, v:number, halfU:number, halfV:number}} hole
   */
  set(collider, portalId, hole) {
    this.clear(portalId);
    let entry = this.entries.get(collider.id);
    if (!entry) {
      entry = { collider, holes: new Map(), generated: [] };
      this.entries.set(collider.id, entry);
    }
    entry.holes.set(portalId, hole);
    this._owner.set(portalId, collider.id);
    this._rebuild(entry);
  }

  /** @private */
  get _owner() {
    if (!this.__owner) this.__owner = new Map();
    return this.__owner;
  }

  clear(portalId) {
    const colliderId = this._owner.get(portalId);
    if (colliderId == null) return;
    this._owner.delete(portalId);
    const entry = this.entries.get(colliderId);
    if (!entry) return;
    entry.holes.delete(portalId);
    if (entry.holes.size === 0) {
      this._dispose(entry);
      entry.collider.enabled = true;
      this.entries.delete(colliderId);
    } else {
      this._rebuild(entry);
    }
  }

  clearAll() {
    for (const entry of this.entries.values()) {
      this._dispose(entry);
      entry.collider.enabled = true;
    }
    this.entries.clear();
    this._owner.clear();
  }

  _dispose(entry) {
    for (const c of entry.generated) this.world.remove(c);
    entry.generated.length = 0;
  }

  _rebuild(entry) {
    this._dispose(entry);
    const host = entry.collider;
    host.enabled = false;

    const he = host.halfExtents.toArray();
    const holes = [...entry.holes.values()];

    // How deep the tunnel goes on each of the six faces.
    const depth = [0, 0, 0].map((_, axis) => {
      const both = holes.some((h) => h.axis === axis && h.sign > 0)
                && holes.some((h) => h.axis === axis && h.sign < 0);
      return Math.min(HOLE_DEPTH, he[axis] * (both ? 0.45 : 0.92));
    });

    const holedFaces = new Map();
    for (const h of holes) {
      const key = `${h.axis}:${h.sign}`;
      if (!holedFaces.has(key)) holedFaces.set(key, { axis: h.axis, sign: h.sign, list: [] });
      holedFaces.get(key).list.push(h);
    }

    // 1. Face shells: the face rect minus every hole in it, extruded inward.
    for (const face of holedFaces.values()) {
      const { axis, sign } = face;
      const u = (axis + 1) % 3;
      const v = (axis + 2) % 3;
      const d = depth[axis];
      const outer = { u0: -he[u], u1: he[u], v0: -he[v], v1: he[v] };
      const cut = face.list.map((h) => ({
        u0: h.u - h.halfU, u1: h.u + h.halfU,
        v0: h.v - h.halfV, v1: h.v + h.halfV,
      }));
      for (const r of subtractRects(outer, cut)) {
        const center = [0, 0, 0];
        const half = [0, 0, 0];
        center[axis] = sign * (he[axis] - d * 0.5);
        half[axis] = d * 0.5;
        center[u] = (r.u0 + r.u1) * 0.5; half[u] = (r.u1 - r.u0) * 0.5;
        center[v] = (r.v0 + r.v1) * 0.5; half[v] = (r.v1 - r.v0) * 0.5;
        entry.generated.push(this._makeFiller(host, center, half));
      }
    }

    // 2. Core: the original box pulled back from every holed face.
    const min = [-he[0], -he[1], -he[2]];
    const max = [he[0], he[1], he[2]];
    for (const face of holedFaces.values()) {
      const d = depth[face.axis];
      if (face.sign > 0) max[face.axis] -= d;
      else min[face.axis] += d;
    }
    if (max[0] - min[0] > 1e-3 && max[1] - min[1] > 1e-3 && max[2] - min[2] > 1e-3) {
      const center = [0, 1, 2].map((i) => (min[i] + max[i]) * 0.5);
      const half = [0, 1, 2].map((i) => (max[i] - min[i]) * 0.5);
      entry.generated.push(this._makeFiller(host, center, half));
    }
  }

  _makeFiller(host, centerLocal, half) {
    _local.set(centerLocal[0], centerLocal[1], centerLocal[2]).applyMatrix4(host.matrix);
    const c = new BoxCollider({
      position: _local.toArray(),
      quaternion: host.quaternion.toArray(),
      halfExtents: half,
      portalable: false,
      tags: ['portal-filler', ...host.tags],
    });
    c.userData = host.userData;
    this.world.add(c);
    return c;
  }
}
